// frontend/modules/in-app-notifications/components/NotificationList.tsx
import React from 'react';
import { List, Pagination, Empty, Spin } from 'antd';
import { InAppNotification } from '../types';
import { NotificationItem } from './NotificationItem';
import { useTheme } from '@/components/common/ThemeProvider';

interface NotificationListProps {
    notifications: InAppNotification[];
    loading?: boolean;
    showPagination?: boolean;
    currentPage?: number;
    pageSize?: number;
    totalElements?: number;
    totalPages?: number;
    onPageChange?: (page: number, size: number) => void;
    onNotificationClick?: (notification: InAppNotification) => void;
    onMarkAsRead?: (id: number) => void;
    onMarkAsAttended?: (id: number) => void;
    onDelete?: (id: number) => void;
}

export const NotificationList: React.FC<NotificationListProps> = ({
    notifications,
    loading = false,
    showPagination = true,
    currentPage = 0,
    pageSize = 20,
    totalElements = 0,
    totalPages,
    onPageChange,
    onNotificationClick,
    onMarkAsRead,
    onMarkAsAttended,
    onDelete
}) => {
    const { dark } = useTheme();

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <Spin size="large" />
            </div>
        );
    }

    if (!notifications || notifications.length === 0) {
        return (
            <Empty
                description="No notifications found"
                className={dark ? 'text-white' : ''}
            />
        );
    }

    return (
        <div className="notification-list">
            <List
                dataSource={notifications}
                renderItem={(notification) => (
                    <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={() => onNotificationClick?.(notification)}
                        onMarkAsRead={() => notification.id && onMarkAsRead?.(notification.id)}
                        onMarkAsAttended={() => notification.id && onMarkAsAttended?.(notification.id)}
                        onDelete={() => notification.id && onDelete?.(notification.id)}
                    />
                )}
                className={dark ? 'dark-list' : ''}
            />

            {showPagination && totalElements > pageSize && (
                <div className="flex justify-center mt-4">
                    <Pagination
                        current={currentPage + 1} // Ant Design uses 1-based pagination
                        pageSize={pageSize}
                        total={totalElements}
                        onChange={(page, size) => onPageChange?.(page - 1, size || pageSize)}
                        showSizeChanger
                        showQuickJumper
                        showTotal={(total, range) =>
                            `${range[0]}-${range[1]} of ${total} notifications`
                        }
                        className={dark ? 'dark-pagination' : ''}
                    />
                </div>
            )}
        </div>
    );
};
